import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal, Form } from 'react-bootstrap';
import { Plus, Pencil, Trash2, GripVertical, X } from 'react-bootstrap-icons';
import { useAdminMenu } from '../../context/AdminMenuContext';

const ModifierGroupModal = ({ show, onHide, editingGroup, onSave }) => {
  const { modifierGroups } = useAdminMenu();
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'single',
    required: false,
    isActive: true,
  });

  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (editingGroup) {
      setFormData({
        name: editingGroup.name,
        type: editingGroup.type,
        required: editingGroup.required,
        isActive: editingGroup.isActive,
      });
      setOptions(editingGroup.options || []);
    } else {
      setFormData({
        name: '',
        type: 'single',
        required: false,
        isActive: true,
      });
      setOptions([]);
    }
  }, [editingGroup, show]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim() || options.length === 0) {
      alert('Vui lòng điền đầy đủ thông tin và thêm ít nhất 1 tuỳ chọn');
      return;
    }

    const group = {
      id: editingGroup?.id || `mod-${Date.now()}`,
      name: formData.name,
      type: formData.type,
      required: formData.required,
      isActive: formData.isActive,
      displayOrder: editingGroup?.displayOrder || modifierGroups.length + 1,
      options,
    };

    onSave(group);
  };

  const addOption = () => {
    setOptions([
      ...options,
      {
        id: `opt-${Date.now()}`,
        name: '',
        price: 0,
        isDefault: false,
      },
    ]);
  };

  const updateOption = (index, updates) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], ...updates };
    setOptions(newOptions);
  };

  const removeOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingGroup ? 'Chỉnh sửa nhóm tuỳ chọn' : 'Thêm nhóm tuỳ chọn'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className="mb-3">
            <Form.Label>Tên nhóm *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="VD: Kích cỡ, Đá, Đường, Topping"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Loại *</Form.Label>
            <Form.Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
            >
              <option value="single">Chọn 1</option>
              <option value="multiple">Chọn nhiều</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="modifier-required"
              label="Bắt buộc chọn"
              checked={formData.required}
              onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="modifier-active"
              label="Hoạt động"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
          </Form.Group>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0">Các tuỳ chọn</Form.Label>
              <Button variant="outline-primary" size="sm" onClick={addOption}>
                <Plus size={16} className="me-1" />
                Thêm tuỳ chọn
              </Button>
            </div>

            <div className="d-flex flex-column gap-2">
              {options.map((option, index) => (
                <div key={option.id} className="d-flex gap-2">
                  <Form.Control
                    placeholder="Tên"
                    value={option.name}
                    onChange={(e) => updateOption(index, { name: e.target.value })}
                    className="flex-grow-1"
                  />
                  <Form.Control
                    type="number"
                    placeholder="Giá"
                    value={option.price}
                    onChange={(e) => updateOption(index, { price: Number(e.target.value) })}
                    style={{ width: '120px' }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeOption(index)}
                  >
                    <X size={16} />
                  </Button>
                </div>
              ))}
              {options.length === 0 && (
                <small className="text-muted">Chưa có tuỳ chọn nào</small>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Huỷ
          </Button>
          <Button variant="primary" type="submit">
            {editingGroup ? 'Cập nhật' : 'Thêm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AdminModifierGroups = () => {
  const {
    modifierGroups,
    createModifierGroup,
    updateModifierGroup,
    deleteModifierGroup,
  } = useAdminMenu();

  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const handleEdit = (group) => {
    setEditingGroup(group);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingGroup(null);
    setShowModal(true);
  };

  const handleSave = async (groupData) => {
    try {
      if (editingGroup) {
        await updateModifierGroup(editingGroup.id, groupData);
      } else {
        await createModifierGroup(groupData);
      }
      setShowModal(false);
      setEditingGroup(null);
    } catch (error) {
      console.error('Error saving modifier group:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc muốn xoá nhóm tuỳ chọn này?')) {
      try {
        await deleteModifierGroup(id);
      } catch (error) {
        console.error('Error deleting modifier group:', error);
      }
    }
  };

  return (
    <>
      <Card className="shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">Nhóm Tuỳ Chọn</h4>
              <small className="text-muted">
                Quản lý các nhóm tuỳ chọn như kích cỡ, đá, đường, topping
              </small>
            </div>
            <Button variant="primary" size="sm" onClick={handleAdd}>
              <Plus size={18} className="me-1" />
              Thêm nhóm
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-column gap-3">
            {modifierGroups.map((group) => (
              <div
                key={group.id}
                className="d-flex align-items-center gap-3 p-3 border rounded bg-light"
              >
                <div className="cursor-grab">
                  <GripVertical size={20} className="text-muted" />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                    <h5 className="mb-0">{group.name}</h5>
                    <Badge bg={group.isActive ? 'success' : 'secondary'} pill>
                      {group.isActive ? 'Hoạt động' : 'Tắt'}
                    </Badge>
                    <Badge bg="info" pill>
                      {group.type === 'single' ? 'Chọn 1' : 'Chọn nhiều'}
                    </Badge>
                    {group.required && (
                      <Badge bg="warning" text="dark" pill>
                        Bắt buộc
                      </Badge>
                    )}
                  </div>
                  <small className="text-muted">
                    {group.options?.length || 0} tuỳ chọn
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(group)}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(group.id)}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {modifierGroups.length === 0 && (
            <div className="text-center py-5 text-muted">
              Chưa có nhóm tuỳ chọn nào
            </div>
          )}
        </Card.Body>
      </Card>

      <ModifierGroupModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingGroup(null);
        }}
        editingGroup={editingGroup}
        onSave={handleSave}
      />
    </>
  );
};

export default AdminModifierGroups;

